import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import PricingMatrix from "@/components/portal/PricingMatrixV2";
import { toast } from "sonner";
import { Route, ChevronDown, ChevronRight, Save, Ban, CheckCircle2 } from "lucide-react";

/**
 * Partner Portal → Rotas Fixas (Iter 47 refactor)
 * Simplificado: input = preço cliente final. Breakdown motorista/DiTours/partner debaixo.
 */
export default function PartnerFixedRoutes() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [platformFeePct, setPlatformFeePct] = useState(15);
  const [partnerPct, setPartnerPct] = useState(0);

  const load = async () => {
    try {
      const { data: d } = await api.get("/partner/fixed-routes");
      setData(d);
      const validKeys = new Set((d.categories || []).map((c) => c.key));
      const filterCats = (obj) => {
        if (!obj || typeof obj !== "object") return obj;
        const out = {};
        for (const [k, v] of Object.entries(obj)) if (validKeys.has(k)) out[k] = v;
        return out;
      };
      const draftMap = {};
      d.routes.forEach((r) => {
        // Migra formato legacy (category_prices flat) para base_prices
        const ov = r._overrides || {};
        if (ov.base_prices || ov.season_prices || ov.available !== undefined) {
          const cleaned = { ...ov };
          if (ov.base_prices) cleaned.base_prices = filterCats(ov.base_prices);
          if (ov.season_prices) {
            const sp = {};
            for (const [sid, entry] of Object.entries(ov.season_prices)) sp[sid] = filterCats(entry);
            cleaned.season_prices = sp;
          }
          draftMap[r.id] = cleaned;
        } else if (Object.keys(ov).length > 0) {
          // Legacy: converte e filtra
          draftMap[r.id] = { base_prices: filterCats(ov) };
        } else {
          draftMap[r.id] = {};
        }
      });
      setDrafts(draftMap);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    // Load partner's effective platform fee for breakdown computation
    api.get("/partner/me").then((r) => {
      const fee = r.data?.effective_ditours_fee_pct ?? r.data?.global_ditours_fee_pct ?? 15;
      setPlatformFeePct(Number(fee) || 15);
      // Extract partner's markup pct from profile
      const markup = r.data?.markup || {};
      const pct = markup.mode === "pct" ? Number(markup.default || markup.executive_markup || 0) : 0;
      setPartnerPct(pct);
    }).catch(() => {});
  }, []);

  const setDraft = (routeId, patch) => setDrafts((d) => ({ ...d, [routeId]: { ...(d[routeId] || {}), ...patch } }));
  const setDraftOverrides = (routeId, newOverrides) => setDrafts((d) => ({ ...d, [routeId]: { ...newOverrides, available: (d[routeId] || {}).available } }));

  const save = async (routeId) => {
    setSavingId(routeId);
    try {
      await api.put(`/partner/fixed-routes/${routeId}/pricing`, drafts[routeId] || {});
      toast.success("Preços actualizados");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  if (!data) return <div className="p-10 text-sm text-slate-500">A carregar...</div>;

  const zoneKeyOf = (r) => (data.zones.find((z) => z.label === r.zone || z.key === (r.zone || "").toLowerCase())?.key) || (r.zone || "").toLowerCase();

  return (
    <div className="p-8 max-w-[1400px]" data-testid="partner-fixed-routes-page">
      <PortalPageHeader
        eyebrow="Catálogo"
        title="Rotas Fixas"
        description="Preços garantidos para rotas com origem/destino fixos. Configure os seus preços por categoria e por temporada. Vazio = usa preço admin. Categorias sem base admin também podem ter preço próprio definido."
      />

      <div className="space-y-4">
        {data.routes.length === 0 && (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Nenhuma rota fixa disponível.</div>
        )}
        {data.routes.map((r) => {
          const draft = drafts[r.id] || {};
          const isExpanded = expanded === r.id;
          const disabled = draft.available === false;
          const basePrices = r.category_prices || {};

          return (
            <div key={r.id} className={`border ${disabled ? "border-slate-300 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid={`ptr-route-${r.id}`}>
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : r.id)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50"
                data-testid={`ptr-route-toggle-${r.id}`}
              >
                <div className="w-10 h-10 border border-[#f59e0b]/30 bg-[#f59e0b]/5 flex items-center justify-center">
                  <Route className="w-5 h-5 text-[#f59e0b]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-[#0a0f1e] truncate">
                    {r.origin} <span className="text-[#f59e0b]">→</span> {r.destination}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    {r.zone && <span className="text-[#f59e0b] font-bold">Zona: {r.zone}</span>}
                    {disabled && <span className="text-red-600 font-bold flex items-center gap-1"><Ban className="w-3 h-3" /> Desactivada</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 font-mono text-xs text-slate-600">
                  {Object.entries(basePrices).slice(0, 3).map(([k, v]) => (
                    <div key={k}>{k}: €{Number(v).toFixed(0)}</div>
                  ))}
                </div>
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="border-t border-slate-100 p-5 space-y-4 bg-slate-50/30">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setDraft(r.id, { available: !disabled ? false : true })}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${disabled ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-red-400 text-red-600"}`}
                      data-testid={`ptr-route-availability-${r.id}`}
                    >
                      {disabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Reactivar rota</> : <><Ban className="w-3.5 h-3.5" /> Desactivar rota</>}
                    </button>
                  </div>

                  {!disabled && (
                    <PricingMatrix
                      basePrices={basePrices}
                      seasons={data.seasons}
                      categories={data.categories}
                      zoneKey={zoneKeyOf(r)}
                      overrides={draft}
                      onChange={(o) => setDraftOverrides(r.id, o)}
                      testIdPrefix={`ptr-route-${r.id}`}
                    />
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => save(r.id)}
                      disabled={savingId === r.id}
                      className="inline-flex items-center gap-2 h-10 px-5 bg-[#f59e0b] text-white font-bold text-xs uppercase tracking-wide hover:bg-[#d97706] disabled:opacity-50"
                      data-testid={`ptr-route-save-${r.id}`}
                    >
                      <Save className="w-3.5 h-3.5" /> {savingId === r.id ? "A guardar..." : "Guardar preços"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
